import moment from "moment";

export interface SabahuSettings {
  enabled: boolean;
  minutesBeforeSunrise: number;
  iqamahMinutes: number;
}

export const SABAHU_MIN_BEFORE_SUNRISE = 20;
/** Ikameti must come at least this many minutes after Sabahu. */
export const IQAMAH_MIN_AFTER_SABAHU = 4;
export const SABAHU_MAX_BEFORE_SUNRISE = 120;
/** Ezani i Sabahut must be at least this many minutes after Imsaku. */
export const SABAHU_MIN_AFTER_IMSAKU = 40;
export const SABAHU_DEFAULT_BEFORE_SUNRISE = 45;
export const SABAHU_DEFAULT_IQAMAH = 20;
/** Ikameti (Sabahu + ikameti minutes) must be at least this many minutes before sunrise. */
export const IQAMAH_MIN_BEFORE_SUNRISE = 20;

export const clampSabahuMinutes = (minutes: number): number =>
  Math.min(
    SABAHU_MAX_BEFORE_SUNRISE,
    Math.max(SABAHU_MIN_BEFORE_SUNRISE, Math.round(minutes))
  );

/** Largest ikameti delay that still leaves IQAMAH_MIN_BEFORE_SUNRISE before sunrise. */
export const maxIqamahMinutes = (minutesBeforeSunrise: number): number =>
  minutesBeforeSunrise - IQAMAH_MIN_BEFORE_SUNRISE;

/** Smallest Sabahu offset that fits the minimum ikameti gap and the 20 min rule. */
export const minSabahuMinutesFor = (iqamahMinutes: number): number =>
  Math.max(
    SABAHU_MIN_BEFORE_SUNRISE,
    iqamahMinutes + IQAMAH_MIN_BEFORE_SUNRISE
  );

/** Safety net for what the TV displays: keeps ikameti within [+4 min after Sabahu, 20 min before sunrise]. */
export const clampIqamahMinutes = (
  iqamahMinutes: number,
  minutesBeforeSunrise: number
): number =>
  Math.min(
    maxIqamahMinutes(minutesBeforeSunrise),
    Math.max(IQAMAH_MIN_AFTER_SABAHU, Math.round(iqamahMinutes))
  );

/** Returns user-facing (red) error messages; empty when the settings are valid. */
export const validateSabahu = (
  minutesBeforeSunrise: number,
  iqamahMinutes: number,
  maxMinutes: number = SABAHU_MAX_BEFORE_SUNRISE
): string[] => {
  const errors: string[] = [];
  if (minutesBeforeSunrise > maxMinutes) {
    errors.push(
      `Ezani i Sabahut duhet të jetë minimum ${SABAHU_MIN_AFTER_IMSAKU} minuta pas Imsakut (maksimumi sot: ${maxMinutes} min para lindjes së diellit).`
    );
  }
  if (minutesBeforeSunrise < SABAHU_MIN_BEFORE_SUNRISE) {
    errors.push(
      `Sabahu nuk mund të jetë më pak se ${SABAHU_MIN_BEFORE_SUNRISE} minuta para lindjes së diellit.`
    );
  }
  if (iqamahMinutes < IQAMAH_MIN_AFTER_SABAHU) {
    errors.push(
      `Ikameti duhet të jetë minimum ${IQAMAH_MIN_AFTER_SABAHU} minuta pas Sabahut.`
    );
  }
  const ikametiBeforeSunrise = minutesBeforeSunrise - iqamahMinutes;
  if (ikametiBeforeSunrise < IQAMAH_MIN_BEFORE_SUNRISE) {
    errors.push(
      `Ikameti bie ${Math.max(0, ikametiBeforeSunrise)} minuta para lindjes së diellit. Duhet të jetë minimum ${IQAMAH_MIN_BEFORE_SUNRISE} minuta (zvogëlo ikametin ose rrite Sabahun).`
    );
  }
  return errors;
};

export const defaultSabahuSettings = (): SabahuSettings => ({
  enabled: false,
  minutesBeforeSunrise: SABAHU_DEFAULT_BEFORE_SUNRISE,
  iqamahMinutes: SABAHU_DEFAULT_IQAMAH,
});

export const readSabahuSettings = (): SabahuSettings => {
  const defaults = defaultSabahuSettings();
  try {
    const formData = JSON.parse(localStorage.getItem("formData") || "null");
    const saved = formData?.sabahu;
    if (!saved) return defaults;
    const minutesBeforeSunrise = Math.max(
      clampSabahuMinutes(
        Number(saved.minutesBeforeSunrise) || defaults.minutesBeforeSunrise
      ),
      minSabahuMinutesFor(IQAMAH_MIN_AFTER_SABAHU)
    );
    return {
      enabled: !!saved.enabled,
      minutesBeforeSunrise,
      iqamahMinutes: clampIqamahMinutes(
        Number.isFinite(Number(saved.iqamahMinutes))
          ? Number(saved.iqamahMinutes)
          : defaults.iqamahMinutes,
        minutesBeforeSunrise
      ),
    };
  } catch {
    return defaults;
  }
};

const toMinutes = (time: string): number => {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

/**
 * Largest "minutes before sunrise" allowed today so Sabahu stays at least
 * SABAHU_MIN_AFTER_IMSAKU minutes after Imsaku (capped by the global maximum).
 */
export const maxSabahuMinutesFor = (
  sunrise?: string,
  imsaku?: string
): number => {
  if (!sunrise || !imsaku) return SABAHU_MAX_BEFORE_SUNRISE;
  return Math.min(
    SABAHU_MAX_BEFORE_SUNRISE,
    toMinutes(sunrise) - toMinutes(imsaku) - SABAHU_MIN_AFTER_IMSAKU
  );
};

/**
 * Today's Sabahu ezan and ikamet times ("HH:mm"), with every rule applied:
 * >= 40 min after Imsaku, >= 20 min before sunrise, ikameti >= 4 min after ezan
 * and >= 20 min before sunrise.
 */
export const resolveSabahuTimes = (
  sunrise: string | undefined,
  imsaku: string | undefined,
  settings: SabahuSettings
): { adhan?: string; iqamah?: string } => {
  if (!sunrise) return {};
  const maxOffset = maxSabahuMinutesFor(sunrise, imsaku);
  const offset = Math.min(
    clampSabahuMinutes(settings.minutesBeforeSunrise),
    maxOffset
  );
  const iqamahMinutes = clampIqamahMinutes(settings.iqamahMinutes, offset);
  const adhan = moment(sunrise, "HH:mm")
    .subtract(offset, "minutes")
    .format("HH:mm");
  const iqamah = moment(adhan, "HH:mm")
    .add(iqamahMinutes, "minutes")
    .format("HH:mm");
  return { adhan, iqamah };
};
